import {
  createContext,
  useContext,
  useState,
  useEffect,
  ReactNode,
  useCallback,
} from 'react'
import { buildApiUrl } from '../lib/api-url'
import type { AccessResponse, CheckAccessResponse } from '../lib/types'

const TOKEN_KEY = 'analytics_dashboard_token'

interface AuthContextType {
  isAuthenticated: boolean
  isLoading: boolean
  login: (password: string) => Promise<{ success: boolean; error?: string }>
  logout: () => void
  token: string | null
}

const AuthContext = createContext<AuthContextType | null>(null)

export function AuthProvider({ children }: { children: ReactNode }) {
  const [token, setToken] = useState<string | null>(null)
  const [isLoading, setIsLoading] = useState(true)

  // Check stored token on mount
  useEffect(() => {
    const checkAuth = async () => {
      const storedToken = localStorage.getItem(TOKEN_KEY)
      if (!storedToken) {
        setIsLoading(false)
        return
      }

      try {
        const response = await fetch(buildApiUrl('/api/check-access'), {
          headers: {
            Authorization: `Bearer ${storedToken}`,
          },
        })
        const data: CheckAccessResponse = await response.json()

        if (data.success) {
          setToken(storedToken)
        } else {
          localStorage.removeItem(TOKEN_KEY)
        }
      } catch {
        localStorage.removeItem(TOKEN_KEY)
      } finally {
        setIsLoading(false)
      }
    }

    checkAuth()
  }, [])

  const login = useCallback(
    async (password: string): Promise<{ success: boolean; error?: string }> => {
      try {
        const response = await fetch(buildApiUrl('/api/access'), {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({ password }),
        })

        const data: AccessResponse = await response.json()

        if (data.success && data.token) {
          localStorage.setItem(TOKEN_KEY, data.token)
          setToken(data.token)
          return { success: true }
        }

        return { success: false, error: data.error || 'Invalid password' }
      } catch {
        return { success: false, error: 'Connection error' }
      }
    },
    []
  )

  const logout = useCallback(() => {
    localStorage.removeItem(TOKEN_KEY)
    setToken(null)
  }, [])

  return (
    <AuthContext.Provider
      value={{
        isAuthenticated: !!token,
        isLoading,
        login,
        logout,
        token,
      }}
    >
      {children}
    </AuthContext.Provider>
  )
}

export function useAuth() {
  const context = useContext(AuthContext)
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider')
  }
  return context
}
